import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  FaTv,
  FaRegSnowflake,
  FaProcedures,
  FaHeartbeat,
  FaUserMd,
  FaBed,
  FaPlus,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import EditModal from "../components/EditModal"; // Ensure you have this component
import AddFacilityModal from "../components/AddFacilityModal";
import AddCareEquipmentModal from "../components/AddCareEquipmentModal";
import AddRoomModal from "../components/AddRoomModal";
import AddBedModal from "../components/AddBedModal";
import AddBedCareEquipmentModal from "../components/AddBedCareEquipmentModal";

// Initial dummy data as state
const initialWards = [
  {
    id: 1,
    wardName: "Cardiology",
    department: "Medical",
    facilityEquipment: [
      {
        id: 1,
        name: "Common TV",
        icon: <FaTv className="text-blue-900 text-2xl" />,
      },
      {
        id: 2,
        name: "Shared AC",
        icon: <FaRegSnowflake className="text-blue-800 text-2xl" />,
      },
    ],
    careEquipment: [
      {
        id: 1,
        name: "Emergency Ventilator",
        icon: <FaProcedures className="text-red-600 text-2xl" />,
      },
      {
        id: 2,
        name: "Oxygen Cylinder",
        icon: <FaProcedures className="text-gray-700 text-2xl" />,
      },
    ],
    rooms: [
      {
        id: 101,
        number: "101A",
        type: "Standard",
        facilityEquipment: [
          {
            id: 1,
            name: "Private AC",
            icon: <FaRegSnowflake className="text-blue-800 text-2xl" />,
          },
          {
            id: 2,
            name: "Mini Fridge",
            icon: <FaProcedures className="text-gray-700 text-2xl" />,
          },
        ],
        careEquipment: [
          {
            id: 1,
            name: "Heart Monitor",
            icon: <FaHeartbeat className="text-red-600 text-2xl" />,
          },
        ],
        beds: [
          {
            id: 1,
            label: "Bed 1",
            careEquipment: [
              {
                id: 1,
                name: "IV Stand",
                icon: <FaProcedures className="text-gray-700 text-2xl" />,
              },
              {
                id: 2,
                name: "Bedside Oxygen",
                icon: <FaProcedures className="text-blue-800 text-2xl" />,
              },
            ],
          },
        ],
      },
    ],
  },
];

const WardDetail = () => {
  const { wardId } = useParams();
  const [wards, setWards] = useState(initialWards);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editType, setEditType] = useState(""); // "ward", "room", "bed"
  const [editItem, setEditItem] = useState(null);

  // Contextual state for modals (for beds/rooms)
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [currentBedId, setCurrentBedId] = useState(null);

  // Modal states for separate forms
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);
  const [showAddCareEquipmentModal, setShowAddCareEquipmentModal] =
    useState(false);
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showAddBedModal, setShowAddBedModal] = useState(false);
  const [showAddBedCareEquipmentModal, setShowAddBedCareEquipmentModal] =
    useState(false);

  // Find the current ward from URL parameter
  const currentWard = wards.find((w) => w.id.toString() === wardId);
  if (!currentWard) {
    return (
      <div className="text-center text-red-600 text-xl font-bold mt-10">
        Ward not found
      </div>
    );
  }

  // Function to open the edit modal
  const openEditModal = (type, item) => {
    setEditType(type);
    setEditItem(item);
    setEditModalOpen(true);
  };

  // Save edit changes
  const saveEdit = (newValue) => {
    if (editType === "ward") {
      const updatedWards = wards.map((w) =>
        w.id === currentWard.id ? { ...w, wardName: newValue } : w
      );
      setWards(updatedWards);
    } else if (editType === "room") {
      const updatedWards = wards.map((w) => {
        if (w.id === currentWard.id) {
          const updatedRooms = w.rooms.map((room) =>
            room.id === editItem.id ? { ...room, number: newValue } : room
          );
          return { ...w, rooms: updatedRooms };
        }
        return w;
      });
      setWards(updatedWards);
    } else if (editType === "bed") {
      const updatedWards = wards.map((w) => {
        if (w.id === currentWard.id) {
          const updatedRooms = w.rooms.map((room) => {
            if (room.beds.some((bed) => bed.id === editItem.id)) {
              const updatedBeds = room.beds.map((bed) =>
                bed.id === editItem.id ? { ...bed, label: newValue } : bed
              );
              return { ...room, beds: updatedBeds };
            }
            return room;
          });
          return { ...w, rooms: updatedRooms };
        }
        return w;
      });
      setWards(updatedWards);
    }
  };

  // --- Ward-level Operations ---
  const deleteWard = (id) => {
    setWards(wards.filter((w) => w.id !== id));
  };

  const updateWard = (id) => {
    openEditModal(
      "ward",
      wards.find((w) => w.id === id)
    );
  };

  // --- Room-level Operations ---
  const handleAddRoom = (wardId) => {
    const newRoom = {
      id: Date.now(),
      number: "New Room",
      type: "Standard",
      facilityEquipment: [],
      careEquipment: [],
      beds: [],
    };
    const updatedWards = wards.map((w) => {
      if (w.id === wardId) {
        return { ...w, rooms: [...w.rooms, newRoom] };
      }
      return w;
    });
    setWards(updatedWards);
  };

  const deleteRoom = (wardId, roomId) => {
    const updatedWards = wards.map((w) => {
      if (w.id === wardId) {
        return { ...w, rooms: w.rooms.filter((room) => room.id !== roomId) };
      }
      return w;
    });
    setWards(updatedWards);
  };

  const updateRoom = (wardId, roomId) => {
    const roomToEdit = currentWard.rooms.find((room) => room.id === roomId);
    if (roomToEdit) {
      openEditModal("room", roomToEdit);
    }
  };

  // --- Bed-level Operations ---
  const handleAddBed = (wardId, roomId) => {
    const newBed = { id: Date.now(), label: "New Bed", careEquipment: [] };
    const updatedWards = wards.map((w) => {
      if (w.id === wardId) {
        const updatedRooms = w.rooms.map((room) => {
          if (room.id === roomId) {
            return { ...room, beds: [...room.beds, newBed] };
          }
          return room;
        });
        return { ...w, rooms: updatedRooms };
      }
      return w;
    });
    setWards(updatedWards);
  };

  const deleteBed = (wardId, roomId, bedId) => {
    const updatedWards = wards.map((w) => {
      if (w.id === wardId) {
        const updatedRooms = w.rooms.map((room) => {
          if (room.id === roomId) {
            return {
              ...room,
              beds: room.beds.filter((bed) => bed.id !== bedId),
            };
          }
          return room;
        });
        return { ...w, rooms: updatedRooms };
      }
      return w;
    });
    setWards(updatedWards);
  };

  const updateBed = (wardId, roomId, bedId) => {
    let targetBed;
    wards.forEach((w) => {
      if (w.id === currentWard.id) {
        w.rooms.forEach((room) => {
          const found = room.beds.find((bed) => bed.id === bedId);
          if (found) {
            targetBed = found;
          }
        });
      }
    });
    if (targetBed) {
      openEditModal("bed", targetBed);
    }
  };

  // --- Equipment-level Operations (Ward Level) ---
  const deleteEquipmentFromWard = (wardId, equipmentId, type = "facility") => {
    const updatedWards = wards.map((w) => {
      if (w.id === currentWard.id) {
        if (type === "facility") {
          return {
            ...w,
            facilityEquipment: w.facilityEquipment.filter(
              (eq) => eq.id !== equipmentId
            ),
          };
        } else {
          return {
            ...w,
            careEquipment: w.careEquipment.filter(
              (eq) => eq.id !== equipmentId
            ),
          };
        }
      }
      return w;
    });
    setWards(updatedWards);
  };

  // --- Equipment-level Operations (Bed Level) ---
  const handleAddBedCareEquipment = (data, roomId, bedId) => {
    const newEquipment = {
      id: data.id,
      name: data.name,
      icon: <FaProcedures className="text-blue-800 text-2xl" />,
    };
    const updatedWards = wards.map((w) => {
      if (w.id === currentWard.id) {
        const updatedRooms = w.rooms.map((room) => {
          if (room.id === roomId) {
            const updatedBeds = room.beds.map((bed) => {
              if (bed.id === bedId) {
                return {
                  ...bed,
                  careEquipment: [...bed.careEquipment, newEquipment],
                };
              }
              return bed;
            });
            return { ...room, beds: updatedBeds };
          }
          return room;
        });
        return { ...w, rooms: updatedRooms };
      }
      return w;
    });
    setWards(updatedWards);
  };

  return (
    <div className="container mx-auto p-8 bg-gray-50 min-h-screen">
      {/* Render Modals */}
      <EditModal
        isOpen={editModalOpen}
        title={
          editType === "ward"
            ? "Edit Ward Name"
            : editType === "room"
              ? "Edit Room Number"
              : "Edit Bed Label"
        }
        fieldLabel={
          editType === "ward"
            ? "Ward Name"
            : editType === "room"
              ? "Room Number"
              : "Bed Label"
        }
        initialValue={
          editType === "ward"
            ? editItem?.wardName
            : editType === "room"
              ? editItem?.number
              : editItem?.label
        }
        onSave={saveEdit}
        onClose={() => setEditModalOpen(false)}
      />

      <AddFacilityModal
        isOpen={showAddFacilityModal}
        onClose={() => setShowAddFacilityModal(false)}
        // onSave={handleAddFacility}
      />
      <AddCareEquipmentModal
        isOpen={showAddCareEquipmentModal}
        onClose={() => setShowAddCareEquipmentModal(false)}
        // onSave={handleAddCareEquipment}
      />
      <AddRoomModal
        isOpen={showAddRoomModal}
        onClose={() => setShowAddRoomModal(false)}
        onSave={handleAddRoom}
      />
      <AddBedModal
        isOpen={showAddBedModal}
        onClose={() => setShowAddBedModal(false)}
        onSave={(data) => handleAddBed(data, currentRoomId)}
      />
      <AddBedCareEquipmentModal
        isOpen={showAddBedCareEquipmentModal}
        onClose={() => setShowAddBedCareEquipmentModal(false)}
        onSave={(data) =>
          handleAddBedCareEquipment(data, currentRoomId, currentBedId)
        }
      />

      {/* Ward Header */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-blue-900">
            {currentWard.wardName} Ward
          </h1>
          <p className="text-gray-700 text-lg">
            Department: {currentWard.department}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => updateWard(currentWard.id)}
            className="text-blue-600 hover:text-blue-800"
          >
            <FaEdit size={24} />
          </button>
          <button
            onClick={() => deleteWard(currentWard.id)}
            className="text-red-600 hover:text-red-800"
          >
            <FaTrash size={24} />
          </button>
        </div>
      </div>

      {/* Ward-Level Equipment */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-blue-900">Ward Facilities</h2>
        <div className="flex items-center space-x-3 mt-2">
          <button
            onClick={() => setShowAddFacilityModal(true)}
            className="text-green-600 hover:text-green-800 flex items-center"
          >
            <FaPlus className="mr-1" /> Add Facility
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-3">
          {currentWard.facilityEquipment.map((equipment) => (
            <div
              key={equipment.id}
              className="p-4 shadow-md rounded-lg bg-white flex items-center justify-between border border-gray-200 hover:shadow-lg transition"
            >
              <div className="flex items-center space-x-2">
                {equipment.icon}
                <p className="text-gray-800 font-medium">{equipment.name}</p>
              </div>
              <button
                onClick={() =>
                  deleteEquipmentFromWard(
                    currentWard.id,
                    equipment.id,
                    "facility"
                  )
                }
                className="text-red-600 hover:text-red-800"
              >
                <FaTrash size={16} />
              </button>
            </div>
          ))}
        </div>

        <h2 className="text-xl font-bold text-blue-900 mt-6">
          Ward Care Equipment
        </h2>
        <div className="flex items-center space-x-3 mt-2">
          <button
            onClick={() => setShowAddCareEquipmentModal(true)}
            className="text-green-600 hover:text-green-800 flex items-center"
          >
            <FaPlus className="mr-1" /> Add Care Equipment
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-3">
          {currentWard.careEquipment.map((equipment) => (
            <div
              key={equipment.id}
              className="p-4 shadow-md rounded-lg bg-white flex items-center justify-between border border-gray-200 hover:shadow-lg transition"
            >
              <div className="flex items-center space-x-2">
                {equipment.icon}
                <p className="text-gray-800 font-medium">{equipment.name}</p>
              </div>
              <button
                onClick={() =>
                  deleteEquipmentFromWard(currentWard.id, equipment.id, "care")
                }
                className="text-red-600 hover:text-red-800"
              >
                <FaTrash size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Rooms Section */}
      <h2 className="text-2xl font-bold text-blue-900 mt-8">
        Rooms in this Ward
      </h2>
      <div className="flex items-center space-x-3 mt-2">
        <button
          onClick={() => setShowAddRoomModal(true)}
          className="text-green-600 hover:text-green-800 flex items-center"
        >
          <FaPlus className="mr-1" /> Add Room
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
        {currentWard.rooms.map((room) => (
          <div
            key={room.id}
            className="p-6 bg-white shadow-lg rounded-lg border border-gray-200 hover:shadow-xl transition relative"
          >
            <div className="absolute top-2 right-2 flex space-x-2">
              <button
                onClick={() => updateRoom(currentWard.id, room.id)}
                className="text-blue-600 hover:text-blue-800"
              >
                <FaEdit size={18} />
              </button>
              <button
                onClick={() => deleteRoom(currentWard.id, room.id)}
                className="text-red-600 hover:text-red-800"
              >
                <FaTrash size={18} />
              </button>
            </div>
            <div className="flex items-center space-x-3 mb-3">
              <FaBed className="text-blue-900 text-3xl" />
              <p className="text-xl font-semibold text-gray-800">
                Room {room.number} ({room.type})
              </p>
            </div>

            {/* Room-Level Equipment */}
            <h3 className="text-md font-bold text-blue-800">Room Facilities</h3>
            {room.facilityEquipment.map((equipment) => (
              <div
                key={equipment.id}
                className="p-2 flex items-center space-x-2 text-gray-700"
              >
                {equipment.icon}
                <p>{equipment.name}</p>
              </div>
            ))}
            <h3 className="text-md font-bold text-blue-800 mt-3">
              Room Care Equipment
            </h3>
            {room.careEquipment.map((equipment) => (
              <div
                key={equipment.id}
                className="p-2 flex items-center space-x-2 text-gray-700"
              >
                {equipment.icon}
                <p>{equipment.name}</p>
              </div>
            ))}

            {/* Beds Section */}
            <h3 className="text-md font-bold text-blue-800 mt-3">Beds</h3>
            <div className="flex items-center space-x-3 mt-2">
              <button
                onClick={() => {
                  setCurrentRoomId(room.id);
                  setShowAddBedModal(true);
                }}
                className="text-green-600 hover:text-green-800 flex items-center"
              >
                <FaPlus className="mr-1" /> Add Bed
              </button>
            </div>
            {room.beds.map((bed) => (
              <div
                key={bed.id}
                className="p-4 bg-gray-100 rounded-lg mt-3 shadow-md border border-gray-200 relative"
              >
                <div className="absolute top-2 right-2 flex space-x-2">
                  <button
                    onClick={() => updateBed(currentWard.id, room.id, bed.id)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FaEdit size={16} />
                  </button>
                  <button
                    onClick={() => deleteBed(currentWard.id, room.id, bed.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FaTrash size={16} />
                  </button>
                </div>
                <div className="flex items-center space-x-3">
                  <FaUserMd className="text-blue-900 text-2xl" />
                  <p className="font-bold text-gray-800">
                    Bed Label: {bed.label}
                  </p>
                </div>
                <div className="flex items-center space-x-3 mt-2">
                  <button
                    onClick={() => {
                      setCurrentRoomId(room.id);
                      setCurrentBedId(bed.id);
                      setShowAddBedCareEquipmentModal(true);
                    }}
                    className="text-green-600 hover:text-green-800 flex items-center"
                  >
                    <FaPlus className="mr-1" /> Add Bed Care Equipment
                  </button>
                </div>
                {bed.careEquipment.map((equipment) => (
                  <div
                    key={equipment.id}
                    className="p-2 flex items-center space-x-2 text-gray-700"
                  >
                    {equipment.icon}
                    <p>{equipment.name}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Modals */}
      <AddFacilityModal
        isOpen={showAddFacilityModal}
        onClose={() => setShowAddFacilityModal(false)}
        // onSave={handleAddFacility}
      />
      <AddCareEquipmentModal
        isOpen={showAddCareEquipmentModal}
        onClose={() => setShowAddCareEquipmentModal(false)}
        // onSave={handleAddCareEquipment}
      />
      <AddRoomModal
        isOpen={showAddRoomModal}
        onClose={() => setShowAddRoomModal(false)}
        onSave={handleAddRoom}
      />
      <AddBedModal
        isOpen={showAddBedModal}
        onClose={() => setShowAddBedModal(false)}
        onSave={(data) => handleAddBed(data, currentRoomId)}
      />
      <AddBedCareEquipmentModal
        isOpen={showAddBedCareEquipmentModal}
        onClose={() => setShowAddBedCareEquipmentModal(false)}
        onSave={(data) =>
          handleAddBedCareEquipment(data, currentRoomId, currentBedId)
        }
      />
    </div>
  );
};

export default WardDetail;
